import * as Sentry from '@sentry/vue'
import { isScoredSlideType } from '@ahaslides-product/plugins-api'
import Config from '@/config'
import { resolveMarketplaceApiUrl } from '@/utils/resolveMarketplaceApiUrl'
// AHA-47349-presenter-can-create-new-slide-type
import { fetchDeveloperSlideTypes, fetchPublishedSlideTypes } from '@/api/developerManifest'
import builtInSlideTypes from '@/utils/slideTypes'

// A built-in slide type identifier (e.g. `ranking`, `poll`). Used to keep a
// developer slug from hijacking a native slide of the same name.
const _isBuiltInType = (type) => builtInSlideTypes.some((s) => s && s.type === type)

export const EMBED_APP_FLAG = 'AHA-42142-embed-app'
export const MARKETPLACE_FLAG = 'AHA-42884-new-slide-market-place'
export const DEVELOPER_SLIDE_TYPE_FLAG = 'AHA-47349-presenter-can-create-new-slide-type'
export const MARKETPLACE_APP_ID = 'marketplace'
// Fetch key for a load that ran without the creator's userId (published types only).
const PUBLISHED_ONLY_FETCH_KEY = 'published-only'

const SET_MANIFESTS = 'SET_MANIFESTS'
const SET_MARKETPLACE_SLIDE_TYPES = 'SET_MARKETPLACE_SLIDE_TYPES'
const SET_MARKETPLACE_LOADED = 'SET_MARKETPLACE_LOADED'
const SET_DEVELOPER_SLIDE_TYPES = 'SET_DEVELOPER_SLIDE_TYPES'

const _state = {
  manifests: [],
  marketplaceSlideTypes: [],
  marketplaceLoaded: false,
  // AHA-47349-presenter-can-create-new-slide-type
  developerSlideTypes: [],
  developerFetchedForUserId: null,
}

const _flattenManifests = (manifests) => {
  const types = []
  manifests.forEach((app) => {
    (app.slideTypes || []).forEach((slideType) => {
      if (slideType && slideType.type) {
        types.push({ ...slideType, appId: app.id, app })
      }
    })
  })
  return types
}

// The marketplace API URL for this page load. Normally the configured one; on a
// sandbox build carrying ?marketplaceSandboxId=<id> (or a previously stored id),
// the same path against that marketplace sandbox's origin. Inert everywhere else.
const _marketplaceApiUrl = () => resolveMarketplaceApiUrl(
  Config.slideTypesMarketplaceUrl,
  Config.audienceUrl,
)

// The marketplace origin (scheme + host) derived from the resolved API URL,
// used to absolutize the relative `audienceUrl` the /api/slide-types-audience
// endpoint ships (e.g. "/plugin/poll/audience"). Deriving it from the SAME
// resolved URL the catalogue was fetched from keeps the plugin iframes on the
// origin the catalogue came from.
const _marketplaceOrigin = () => {
  try {
    return new URL(_marketplaceApiUrl()).origin
  } catch {
    return ''
  }
}

// Normalize a /api/slide-types-audience payload entry into the FLAT shape the
// store's consumers expect (a top-level `type`, `setting`, and an ABSOLUTE
// `audienceUrl` — read by customSlideTypeByType → SlidePluginContent/Presentation).
//
// The endpoint nests the slide config under `ahaConfig` and ships a RELATIVE
// `audienceUrl`, so we lift `ahaConfig` to the top level and absolutize the URL
// against the marketplace origin. Shape-agnostic: a legacy flat entry (no
// `ahaConfig`) and an already-absolute `audienceUrl` both pass through unchanged.
//
// Only `marketplace/<slug>` plugin slides are kept — matching the previous
// behaviour where solely marketplace entries (the ones carrying a top-level
// `type`) reached the store. Native presenter types resolve through the built-in
// slideTypes registry instead, so they are intentionally excluded here.
const _normalizeMarketplace = (raw) => {
  const origin = _marketplaceOrigin()
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null
      const { ahaConfig, ...topLevel } = entry
      const cfg = ahaConfig && typeof ahaConfig === 'object' ? ahaConfig : entry
      const audienceUrl = typeof cfg.audienceUrl === 'string' && cfg.audienceUrl.startsWith('/') && origin
        ? `${origin}${cfg.audienceUrl}`
        : cfg.audienceUrl
      // Merge top-level metadata (e.g. `name`) with the ahaConfig slide config so
      // BOTH survive; ahaConfig wins on overlap (type/slideType/setting/plugin).
      return {
        ...topLevel, ...cfg, plugin: entry.plugin ?? cfg.plugin, audienceUrl,
      }
    })
    .filter((t) => t && typeof t.type === 'string' && t.type.startsWith('marketplace/'))
}

const _getters = {
  manifests: (state) => state.manifests,
  marketplaceSlideTypes: (state) => state.marketplaceSlideTypes,
  customSlideTypes: (state) => {
    const flagTypes = _flattenManifests(state.manifests)
    const seen = new Set(flagTypes.map((t) => t.type))
    const marketplaceTypes = state.marketplaceSlideTypes
      .filter((t) => t && t.type && !seen.has(t.type))
      .map((t) => ({ ...t, appId: MARKETPLACE_APP_ID }))
    // AHA-47349-presenter-can-create-new-slide-type — the creator's developer
    // slide types, de-duped against flag + marketplace types.
    const seenAll = new Set([...seen, ...marketplaceTypes.map((t) => t.type)])
    const developerTypes = state.developerSlideTypes
      .filter((t) => t && t.type && !seenAll.has(t.type))
      .map((t) => ({ ...t, appId: MARKETPLACE_APP_ID }))
    return [...flagTypes, ...marketplaceTypes, ...developerTypes]
  },
  customSlideTypeByType: (_, getters) => (type) => {
    if (!type) return null
    const found = getters.customSlideTypes.find((s) => s.type === type)
    if (found) return found
    // AHA-47349-presenter-can-create-new-slide-type — fall back ONLY for developer
    // slide types (loaded by loadDeveloperSlideTypes: published types always, the
    // creator's drafts only with the flag on). A developer slide's stored `slide.type` may
    // be the pageId (the entry's `type`), the bare `slug` (for slides created
    // before pageId-as-type), or either form `marketplace/`-prefixed. Scoping to
    // `isDeveloperSlideType` keeps flag-off behaviour unchanged and never lets a
    // marketplace entry be matched here by slug or prefix.
    // The pageId `type` is random and safe to match; the slug is developer-chosen,
    // so it must NOT match a bare slug that collides with a built-in slide type
    // (`ranking`, `poll`, …) — that would hijack every native slide of that type.
    const bare = String(type).replace(/^marketplace\//, '')
    const slugMatchable = !_isBuiltInType(bare)
    // A published type belongs to ANY creator and its slug is free text, so it
    // matches only by its exact `type` (its stable pageId), never by slug. Slides
    // created while published types were keyed by `publishedKey` still match on it.
    return getters.customSlideTypes.find((s) => s.isDeveloperSlideType
      && (s.type === bare
        || (s.isPublishedSlideType && Boolean(s.publishedKey) && (s.publishedKey === type || s.publishedKey === bare))
        || (!s.isPublishedSlideType && slugMatchable && (s.slug === type || s.slug === bare)))) || null
  },
  isScoredSlide: (_, getters) => (slide) => {
    if (!slide) return false
    return isScoredSlideType(getters.customSlideTypeByType(slide.type))
  },
  // Full-quiz plugin: this plugin slide type opts into reusing the host's native
  // quiz flow via `setting.enableQuizSetting`. When true the audience shows host
  // lobby/rule/countdown screens and mounts the plugin iframe only at
  // QUESTION/RESULT (see SlidePluginContent). Requires isQuizSlide (scored) too —
  // matching the presenter getter `slidePlugin/isSlideActiveFullQuizPlugin` — so a
  // non-scored plugin never gets the host phase screens (its quizStatus never
  // advances via the quiz-lobby socket path, which is itself isQuizSlide-gated).
  isFullQuizPluginSlide: (_, getters, __, rootGetters) => (slide) => {
    if (!slide) return false
    return Boolean(getters.customSlideTypeByType(slide.type)?.setting?.enableQuizSetting === true)
      && rootGetters.isQuizSlide(slide)
  },
  // Question-in-iframe opt-in: when a plugin slide type declares
  // `setting.enableQuestionInIframe`, the plugin iframe renders the WHOLE
  // question (title + image) itself, so the host suppresses its native question
  // title/image and forwards title/image into slideProps (see SlidePluginMixin +
  // PickAnswerContent). Absent the flag the host keeps rendering the native
  // question (incl. #885's native image), leaving every other slide unaffected.
  // Quiz-only (AHAM-512): mirror isFullQuizPluginSlide's `&& isQuizSlide` so the
  // feature never applies to a non-quiz plugin slide.
  isQuestionRenderedInIframe: (_, getters, __, rootGetters) => (slide) => Boolean(
    getters.customSlideTypeByType(slide?.type)?.setting?.enableQuestionInIframe === true
      && rootGetters.isQuizSlide(slide),
  ),
}

const _parseManifests = (payload) => {
  if (!payload?.value) return []
  try {
    const parsed = typeof payload.value === 'string' ? JSON.parse(payload.value) : payload.value
    if (!Array.isArray(parsed)) return []
    return parsed.filter((item) => item && typeof item === 'object')
  } catch (error) {
    Sentry.captureException(error, { tags: { source: 'embed-app.module.js', flag: EMBED_APP_FLAG } })
    return []
  }
}

const actions = {
  loadManifests({ commit, rootGetters }) {
    const variant = rootGetters['featureToggles/getVariant'](EMBED_APP_FLAG)
    if (!variant?.enabled) {
      commit(SET_MANIFESTS, [])
      return
    }

    const manifests = _parseManifests(variant.payload)
    commit(SET_MANIFESTS, manifests)
  },

  async loadMarketplaceSlideTypes({ commit, state, rootGetters }) {
    const enabled = rootGetters['featureToggles/isFeatureEnabled'](MARKETPLACE_FLAG)
    if (!enabled) {
      commit(SET_MARKETPLACE_SLIDE_TYPES, [])
      commit(SET_MARKETPLACE_LOADED, false)
      return
    }

    // The embed-app plugin re-dispatches this on every feature-toggle refresh
    // (Unleash `ready` plus each `updateContext`). Fetch the catalog at most
    // once per enabled session so a client that can't reach the endpoint does
    // not re-request it — and re-report it — on every refresh. Set the flag
    // before awaiting so concurrent dispatches don't race a second fetch.
    if (state.marketplaceLoaded) return
    commit(SET_MARKETPLACE_LOADED, true)

    try {
      const response = await fetch(_marketplaceApiUrl())
      if (!response.ok) throw new Error(`Marketplace fetch failed: ${response.status}`)
      const body = await response.json()
      const raw = Array.isArray(body?.slideTypes) ? body.slideTypes : []
      commit(SET_MARKETPLACE_SLIDE_TYPES, _normalizeMarketplace(raw))
    } catch (error) {
      // `TypeError: Failed to fetch` is a browser-level network failure — the
      // request never reached the server (unreachable CDN, offline client,
      // ad-blocker, or regional filtering). It's expected and already handled
      // gracefully below, so don't report it to Sentry; it's noise, not a bug.
      if (!(error instanceof TypeError)) {
        Sentry.captureException(error, { tags: { source: 'embed-app.module.js', flag: MARKETPLACE_FLAG } })
      }
      commit(SET_MARKETPLACE_SLIDE_TYPES, [])
    }
  },

  // AHA-47349-presenter-can-create-new-slide-type — merge the CREATOR's developer
  // slide types (the ones they've made, from the backend's public user-manifests
  // endpoint) into the catalogue so the audience can render them. Keyed by the
  // presentation creator's userId (the audience may be a guest). Published types
  // are open to every presenter, so they load even with the flag off (and without
  // a userId); only the creator's drafts need the flag. Already done / any
  // failure => no-op, and the action never rejects so the join path is unaffected.
  async loadDeveloperSlideTypes({
    commit, state, rootGetters, rootState,
  }, payload) {
    try {
      const canLoadDrafts = Boolean(rootGetters['featureToggles/isFeatureEnabled'](DEVELOPER_SLIDE_TYPE_FLAG))
      // Prefer the creator userId passed by the loader (rootState.presentation may
      // not be committed yet); fall back to the store.
      const userId = canLoadDrafts
        ? ((payload && payload.userId) || (rootState.presentation && rootState.presentation.userId))
        : null
      // Published types don't depend on the creator, so they load even before the
      // creator's userId is known; the drafts join once it is (a new fetch key).
      const fetchKey = userId || PUBLISHED_ONLY_FETCH_KEY
      if (state.developerFetchedForUserId === fetchKey) return
      // Mark before awaiting so concurrent dispatches don't double-fetch.
      commit(SET_DEVELOPER_SLIDE_TYPES, { userId: fetchKey, slideTypes: state.developerSlideTypes })
      // The creator's drafts, then every published slide type — same shape, one list.
      const [developerTypes, publishedTypes] = await Promise.all([
        userId ? fetchDeveloperSlideTypes({ userId }) : [],
        fetchPublishedSlideTypes(),
      ])
      commit(SET_DEVELOPER_SLIDE_TYPES, { userId: fetchKey, slideTypes: [...developerTypes, ...publishedTypes] })
    } catch (error) {
      // best-effort; keep the catalogue as-is
    }
  },
}

/* eslint-disable no-param-reassign */
const mutations = {
  [SET_MANIFESTS](state, manifests) {
    state.manifests = manifests
  },
  [SET_MARKETPLACE_SLIDE_TYPES](state, slideTypes) {
    state.marketplaceSlideTypes = slideTypes
  },
  [SET_MARKETPLACE_LOADED](state, loaded) {
    state.marketplaceLoaded = loaded
  },
  // AHA-47349-presenter-can-create-new-slide-type
  [SET_DEVELOPER_SLIDE_TYPES](state, { userId, slideTypes }) {
    state.developerFetchedForUserId = userId
    state.developerSlideTypes = slideTypes || []
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
